import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, InputNumber, Select, Checkbox,
  Tag, Space, Typography, Tabs, Drawer, Descriptions, Progress, Row, Col,
  Spin, message, Popconfirm, Alert, Divider, Timeline, Badge, Steps,
  Result,
} from 'antd';
import {
  ToolOutlined, PlusOutlined, EditOutlined, DeleteOutlined, PlayCircleOutlined,
  ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, SyncOutlined,
  GlobalOutlined, FileTextOutlined, CodeOutlined, SafetyCertificateOutlined,
  CloudServerOutlined, EyeOutlined, ThunderboltOutlined, ArrowRightOutlined, DollarOutlined, GiftOutlined, RocketOutlined, SendOutlined,
  ExclamationCircleOutlined, SaveOutlined, WarningOutlined, DatabaseOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { aeoFixAPI, aeoScannerAPI, billingAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;

const ISSUE_OPTIONS = [
  { value: 'robots_txt', label: 'robots.txt', desc: '修正 AI 爬蟲封鎖問題，開放 GPTBot / ClaudeBot / PerplexityBot', icon: <FileTextOutlined /> },
  { value: 'sitemap_xml', label: 'sitemap.xml', desc: '重建或更新網站地圖，確保爬蟲能發現所有頁面', icon: <GlobalOutlined /> },
  { value: 'llms_txt', label: 'llms.txt', desc: '建立 AI 可讀說明文件，提升品牌在 LLM 的識別度', icon: <FileTextOutlined /> },
  { value: 'json_ld', label: 'JSON-LD Schema', desc: '在首頁注入 Organization + FAQ 結構化資料', icon: <CodeOutlined /> },
  { value: 'bot_access', label: '爬蟲封鎖修復', desc: '快速修正 robots.txt 中封鎖 AI 爬蟲的規則', icon: <SafetyCertificateOutlined /> },
];

const STATUS_COLORS = { pending: 'default', running: 'processing', done: 'success', failed: 'error' };
const STATUS_LABELS = { pending: '等待中', running: '執行中', done: '完成', failed: '失敗' };
const scoreColor = (s) => s >= 80 ? '#52c41a' : s >= 60 ? '#1890ff' : s >= 40 ? '#faad14' : '#f5222d';

// How old a backup can be and still be considered "recent" (hours)
const BACKUP_FRESH_HOURS = 24;

function isBackupFresh(backup) {
  if (!backup || backup.status !== 'done') return false;
  const age = (Date.now() - new Date(backup.created_at).getTime()) / 3600000;
  return age < BACKUP_FRESH_HOURS;
}

// ── Backup Status Badge (used in multiple places) ────────────────────────

function BackupStatusBadge({ backup, loading }) {
  if (loading) return <Spin size="small" />;
  if (!backup) return <Tag color="default">尚無備份</Tag>;
  if (backup.status === 'running') return <Tag color="processing" icon={<SyncOutlined spin />}>備份中...</Tag>;
  if (backup.status === 'failed') return <Tag color="error" icon={<CloseCircleOutlined />}>備份失敗</Tag>;
  if (backup.status === 'done') {
    const fresh = isBackupFresh(backup);
    return (
      <Tag color={fresh ? 'success' : 'warning'} icon={<CheckCircleOutlined />}>
        {fresh ? '已備份' : '備份較舊'} · {backup.backup_size || '?'}
        {' · '}{new Date(backup.created_at).toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
      </Tag>
    );
  }
  return null;
}

// ── SSH Target Form Modal ─────────────────────────────────────────────────

function TargetModal({ open, onClose, onSaved, editing }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (editing) form.setFieldsValue(editing);
    }
  }, [open, editing, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        await aeoFixAPI.updateTarget(editing.id, values);
        message.success('目標已更新');
      } else {
        await aeoFixAPI.createTarget(values);
        message.success('目標已建立');
      }
      onSaved();
      onClose();
    } catch (e) {
      if (e?.errorFields) return;
      message.error('儲存失敗：' + (e.message || '未知錯誤'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={editing ? '編輯 SSH 目標' : '新增 SSH 目標'}
      onCancel={onClose}
      onOk={handleOk}
      okText="儲存"
      cancelText="取消"
      confirmLoading={saving}
    >
      <Form form={form} layout="vertical" initialValues={{ ssh_user: 'root', ssh_port: 22, web_root: '/var/www/html' }}>
        <Form.Item name="domain" label="網域" rules={[{ required: true, message: '必填' }]}>
          <Input placeholder="example.com" />
        </Form.Item>
        <Row gutter={12}>
          <Col span={14}>
            <Form.Item name="ssh_host" label="SSH Host / IP" rules={[{ required: true, message: '必填' }]}>
              <Input placeholder="192.168.1.100" />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name="ssh_user" label="SSH 用戶">
              <Input placeholder="root" />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name="ssh_port" label="Port">
              <InputNumber style={{ width: '100%' }} placeholder="22" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="web_root" label="Web 根目錄">
          <Input placeholder="/var/www/html" />
        </Form.Item>
        <Form.Item name="notes" label="備註">
          <Input.TextArea rows={2} placeholder="選填備註" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Create Fix Job Modal — with backup-first gate ─────────────────────────
//
// Modal states:
//   'form'           → normal form (scan + target + issues)
//   'backup_warn'    → user clicked submit but target has no fresh backup
//   'backup_running' → backup SSH job is in progress
//   'backup_done'    → backup succeeded, ready to proceed with fix
//   'backup_failed'  → backup failed, offer skip or retry

function CreateJobModal({ open, onClose, onCreated, preloadScanId }) {
  const [form] = Form.useForm();
  const [scans, setScans] = useState([]);
  const [targets, setTargets] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [selectedTargetId, setSelectedTargetId] = useState(null);
  const [latestBackup, setLatestBackup] = useState(null);
  const [loadingBackup, setLoadingBackup] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Issues already fixed by a prior done job for the selected scan
  const [fixedIssues, setFixedIssues] = useState(new Set());
  // modal state machine
  const [step, setStep] = useState('form'); // 'form'|'backup_warn'|'backup_running'|'backup_done'|'backup_failed'
  const [runningBackup, setRunningBackup] = useState(null);
  const backupPollRef = useRef(null);

  // Load scans + targets when modal opens
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setStep('form');
    setSelectedScan(null);
    setSelectedTargetId(null);
    setLatestBackup(null);
    setRunningBackup(null);
    setFixedIssues(new Set());

    Promise.all([aeoScannerAPI.list(), aeoFixAPI.listTargets()])
      .then(([s, t]) => {
        const completed = (s.data || []).filter(x => x.status === 'completed');
        setScans(completed);
        setTargets(t.data || []);
        if (preloadScanId) {
          form.setFieldValue('scan_id', preloadScanId);
          const scan = completed.find(x => x.id === preloadScanId);
          if (scan) {
            setSelectedScan(scan);
            // Seed resolved from scan data immediately (no async delay)
            const scanResolved = new Set();
            const cf = scan.analysis_result?.crawl_friendliness || {};
            const cq = scan.analysis_result?.content_quality || {};
            const bots = cf.bots || {};
            if (cf.robots_txt_exists) scanResolved.add('robots_txt');
            if (cf.sitemap_exists) scanResolved.add('sitemap_xml');
            if (cf.llms_txt_exists) scanResolved.add('llms_txt');
            if (cq.has_json_ld) scanResolved.add('json_ld');
            if (!Object.values(bots).some(s => s === 'blocked')) scanResolved.add('bot_access');
            setFixedIssues(scanResolved);
          }
          // Also merge in prior done jobs for this scan
          aeoFixAPI.listJobs(preloadScanId)
            .then(res => {
              const doneJobs = (res.data || []).filter(j => j.status === 'done');
              if (doneJobs.length) {
                setFixedIssues(prev => {
                  const merged = new Set(prev);
                  doneJobs.forEach(j => (j.selected_issues || []).forEach(i => merged.add(i)));
                  return merged;
                });
              }
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [open, preloadScanId, form]);

  // Fetch latest backup when target changes
  const fetchLatestBackup = useCallback(async (targetId) => {
    if (!targetId) { setLatestBackup(null); return; }
    setLoadingBackup(true);
    try {
      const res = await aeoFixAPI.listBackups(targetId);
      const list = res.data || [];
      setLatestBackup(list[0] || null);
    } catch (e) {
      setLatestBackup(null);
    } finally {
      setLoadingBackup(false);
    }
  }, []);

  const handleTargetChange = (id) => {
    setSelectedTargetId(id);
    fetchLatestBackup(id);
  };

  // Build resolved set from the scan's own analysis (ground truth)
  const getResolvedFromScan = (scan) => {
    const resolved = new Set();
    if (!scan?.analysis_result) return resolved;
    const cf = scan.analysis_result.crawl_friendliness || {};
    const cq = scan.analysis_result.content_quality || {};
    const bots = cf.bots || {};
    if (cf.robots_txt_exists) resolved.add('robots_txt');
    if (cf.sitemap_exists) resolved.add('sitemap_xml');
    if (cf.llms_txt_exists) resolved.add('llms_txt');
    if (cq.has_json_ld) resolved.add('json_ld');
    // bot_access: resolved if no bots are blocked
    if (!Object.values(bots).some(s => s === 'blocked')) resolved.add('bot_access');
    return resolved;
  };

  const handleScanChange = async (id) => {
    const scan = scans.find(x => x.id === id);
    setSelectedScan(scan || null);
    form.setFieldValue('selected_issues', []);
    // Start with what the scan itself reports as already OK
    const scanResolved = getResolvedFromScan(scan);
    // Also fetch prior done jobs for this scan and union them in
    const merged = new Set(scanResolved);
    if (id) {
      try {
        const res = await aeoFixAPI.listJobs(id);
        const doneJobs = (res.data || []).filter(j => j.status === 'done');
        doneJobs.forEach(j => (j.selected_issues || []).forEach(issue => merged.add(issue)));
      } catch (e) { /* ignore */ }
    }
    setFixedIssues(merged);
  };

  const getDefaultIssues = () => {
    if (!selectedScan?.analysis_result) return [];
    const cf = selectedScan.analysis_result.crawl_friendliness || {};
    const cq = selectedScan.analysis_result.content_quality || {};
    const issues = [];
    if (!cf.robots_txt_exists) issues.push('robots_txt');
    if (!cf.sitemap_exists) issues.push('sitemap_xml');
    if (!cf.llms_txt_exists) issues.push('llms_txt');
    if (!cq.has_json_ld) issues.push('json_ld');
    const bots = cf.bots || {};
    if (Object.values(bots).some(s => s === 'blocked')) issues.push('bot_access');
    // Only suggest issues that are still unresolved
    return issues.filter(i => !fixedIssues.has(i));
  };

  // ── Backup polling ─────────────────────────────────────────────────────

  const startBackupPoll = useCallback((backupId) => {
    clearInterval(backupPollRef.current);
    backupPollRef.current = setInterval(async () => {
      try {
        const res = await aeoFixAPI.getBackup(backupId);
        const b = res.data;
        setRunningBackup(b);
        if (b.status === 'done') {
          clearInterval(backupPollRef.current);
          setLatestBackup(b);
          setStep('backup_done');
          message.success('備份完成！現在可以安全地執行修復。');
        } else if (b.status === 'failed') {
          clearInterval(backupPollRef.current);
          setStep('backup_failed');
        }
      } catch (e) { /* ignore */ }
    }, 3000);
  }, []);

  useEffect(() => () => clearInterval(backupPollRef.current), []);

  // ── Run backup ─────────────────────────────────────────────────────────

  const handleRunBackup = async () => {
    setStep('backup_running');
    try {
      const res = await aeoFixAPI.startBackup(selectedTargetId);
      const b = res.data;
      setRunningBackup(b);
      startBackupPoll(b.id);
    } catch (e) {
      setStep('backup_failed');
      setRunningBackup({ status: 'failed', error_message: e.message });
    }
  };

  // ── Submit fix job ─────────────────────────────────────────────────────

  const doCreateJob = async () => {
    try {
      const values = await form.validateFields();
      if (!values.selected_issues?.length) {
        message.warning('請至少選擇一個修復項目');
        return;
      }
      setSubmitting(true);
      const res = await aeoFixAPI.createJob({
        scan_id: values.scan_id,
        target_id: values.target_id,
        selected_issues: values.selected_issues,
      });
      message.success('修復工作已啟動！');
      onCreated(res.data);
      onClose();
    } catch (e) {
      if (e?.errorFields) return;
      message.error('建立失敗：' + (e.message || '未知錯誤'));
    } finally {
      setSubmitting(false);
    }
  };

  // When user clicks "啟動修復"
  const handleSubmit = async () => {
    // First validate the form
    try {
      await form.validateFields();
    } catch (e) {
      return;
    }
    const issues = form.getFieldValue('selected_issues') || [];
    if (!issues.length) { message.warning('請至少選擇一個修復項目'); return; }

    // Check if backup is fresh
    if (isBackupFresh(latestBackup)) {
      // Backup is good → proceed
      doCreateJob();
    } else {
      // No fresh backup → show backup warning step
      setStep('backup_warn');
    }
  };

  // Skip backup confirmation
  const handleSkipBackup = () => {
    Modal.confirm({
      title: '確定略過備份？',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <Paragraph>略過備份意味著若修復出現問題，<Text strong style={{ color: '#f5222d' }}>將無法一鍵回滾</Text>至原始狀態。</Paragraph>
          <Paragraph type="secondary">此操作建議僅在確認目標伺服器已有其他備份機制時使用。</Paragraph>
        </div>
      ),
      okText: '確認略過，直接修復',
      okButtonProps: { danger: true },
      cancelText: '取消，先備份',
      onOk: () => {
        setStep('form');
        doCreateJob();
      },
    });
  };

  const defaultIssues = getDefaultIssues();
  const targetInfo = targets.find(t => t.id === selectedTargetId);

  // ── Step indicator (shown when not in 'form' state) ────────────────────

  const stepItems = [
    {
      title: '備份',
      icon: step === 'backup_running' ? <SyncOutlined spin /> :
        step === 'backup_done' ? <CheckCircleOutlined /> :
          step === 'backup_failed' ? <CloseCircleOutlined /> :
            <SaveOutlined />,
      status: step === 'backup_done' ? 'finish' :
        step === 'backup_failed' ? 'error' :
          step === 'backup_running' ? 'process' : 'wait',
    },
    {
      title: '修復',
      status: step === 'backup_done' ? 'process' : 'wait',
    },
  ];

  // ── Backup warning content ─────────────────────────────────────────────

  const renderBackupSection = () => {
    if (step === 'form') return null;

    return (
      <div style={{ marginBottom: 20 }}>
        <Steps size="small" current={step === 'backup_done' ? 1 : 0} items={stepItems} style={{ marginBottom: 20 }} />

        {step === 'backup_warn' && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginBottom: 12 }}
            message="尚未對此目標執行備份"
            description={
              <div>
                <Paragraph style={{ margin: '8px 0 4px' }}>
                  建議在修復前先備份 <Text code>{targetInfo?.web_root || '/var/www/html'}</Text>，
                  以便在修復出現問題時能快速回滾。
                </Paragraph>
                <Space>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleRunBackup}
                  >
                    立即備份 ({targetInfo?.domain})
                  </Button>
                  <Button type="link" danger onClick={handleSkipBackup}>
                    略過備份（風險自負）
                  </Button>
                </Space>
              </div>
            }
          />
        )}

        {step === 'backup_running' && (
          <Alert
            type="info"
            showIcon
            icon={<SyncOutlined spin />}
            message={
              <Space>
                <span>正在備份 <Text strong>{targetInfo?.domain}</Text> 的網站目錄...</span>
                <Text type="secondary">SSH 連線中，請稍候</Text>
              </Space>
            }
            description={
              <div style={{
                background: '#1a1a2e', color: '#a8ff78', fontFamily: 'monospace',
                padding: '8px 12px', borderRadius: 4, marginTop: 8, fontSize: 12,
              }}>
                <div>→ 連線至 {targetInfo?.ssh_host}:{targetInfo?.ssh_port}</div>
                <div>→ 執行: tar -czf /tmp/aeo_backups/webroot_*.tar.gz {targetInfo?.web_root}</div>
                <div style={{ color: '#ffd93d' }}>▌ 備份進行中...</div>
              </div>
            }
            style={{ marginBottom: 12 }}
          />
        )}

        {step === 'backup_done' && (
          <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            message="備份完成！"
            description={
              <Space wrap>
                <span>路徑：<Text code>{runningBackup?.backup_path}</Text></span>
                <Tag color="green">{runningBackup?.backup_size || '?'}</Tag>
                <Tag>{runningBackup?.file_count || 0} 個檔案</Tag>
                <Text type="secondary">現在可以安全執行修復</Text>
              </Space>
            }
            style={{ marginBottom: 12 }}
          />
        )}

        {step === 'backup_failed' && (
          <Alert
            type="error"
            showIcon
            icon={<CloseCircleOutlined />}
            message="備份失敗"
            description={
              <div>
                <Paragraph style={{ margin: '8px 0 8px', color: '#f5222d' }}>
                  {runningBackup?.error_message || '未知錯誤'}
                </Paragraph>
                <Space>
                  <Button icon={<ReloadOutlined />} onClick={handleRunBackup}>
                    重試備份
                  </Button>
                  <Button type="link" danger onClick={handleSkipBackup}>
                    略過備份（風險自負）
                  </Button>
                </Space>
              </div>
            }
            style={{ marginBottom: 12 }}
          />
        )}

        <Divider style={{ margin: '12px 0' }} />
      </div>
    );
  };

  // ── Footer buttons ─────────────────────────────────────────────────────

  const modalFooter = () => {
    const cancelBtn = <Button key="cancel" onClick={onClose}>取消</Button>;

    if (step === 'backup_running') {
      return [cancelBtn, <Button key="submit" type="primary" disabled loading>修復中備份...</Button>];
    }
    if (step === 'backup_warn') {
      return [cancelBtn];
    }
    const submitDisabled = step === 'backup_running';
    return [
      cancelBtn,
      <Button
        key="submit"
        type="primary"
        icon={<ThunderboltOutlined />}
        loading={submitting}
        disabled={submitDisabled}
        onClick={handleSubmit}
      >
        啟動修復
      </Button>,
    ];
  };

  return (
    <Modal
      open={open}
      title={<Space><ThunderboltOutlined /><span>建立 AI 自動修復工作</span></Space>}
      onCancel={onClose}
      footer={modalFooter()}
      width={660}
      destroyOnClose
    >
      {renderBackupSection()}

      <Form form={form} layout="vertical">
        <Form.Item name="scan_id" label="選擇掃描結果" rules={[{ required: true, message: '必填' }]}>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="選擇一個已完成的 AEO 掃描"
            onChange={handleScanChange}
            options={scans.map(s => ({
              value: s.id,
              label: `[${s.grade || '?'}] ${s.domain} — ${new Date(s.created_at).toLocaleDateString('zh-TW')}`,
            }))}
          />
        </Form.Item>

        {selectedScan && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={
              <Space>
                <span>總分：<Text strong style={{ color: scoreColor(selectedScan.overall_score) }}>{selectedScan.overall_score}</Text></span>
                <span>評級：<Tag color={selectedScan.grade === 'A' ? 'green' : selectedScan.grade === 'B' ? 'blue' : selectedScan.grade === 'C' ? 'gold' : 'red'}>{selectedScan.grade}</Tag></span>
                {defaultIssues.length > 0 && <span>偵測到 <Text strong>{defaultIssues.length}</Text> 個問題</span>}
              </Space>
            }
          />
        )}

        <Form.Item name="target_id" label="SSH 目標伺服器" rules={[{ required: true, message: '必填' }]}>
          <Select
            placeholder="選擇要修復的伺服器"
            onChange={handleTargetChange}
            options={targets.map(t => ({
              value: t.id,
              label: `${t.domain} — ${t.ssh_host}:${t.ssh_port}`,
            }))}
          />
        </Form.Item>

        {/* Backup status for selected target */}
        {selectedTargetId && step === 'form' && (
          <div style={{
            background: isBackupFresh(latestBackup) ? '#f6ffed' : '#fffbe6',
            border: `1px solid ${isBackupFresh(latestBackup) ? '#b7eb8f' : '#ffe58f'}`,
            borderRadius: 6, padding: '8px 12px', marginBottom: 16,
          }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Space>
                  <SaveOutlined style={{ color: isBackupFresh(latestBackup) ? '#52c41a' : '#faad14' }} />
                  <Text strong style={{ fontSize: 13 }}>目標備份狀態：</Text>
                  <BackupStatusBadge backup={latestBackup} loading={loadingBackup} />
                </Space>
              </Col>
              {!isBackupFresh(latestBackup) && (
                <Col>
                  <Text type="secondary" style={{ fontSize: 12 }}>修復前建議備份</Text>
                </Col>
              )}
            </Row>
          </div>
        )}

        <Form.Item
          name="selected_issues"
          label="選擇要修復的問題"
          initialValue={[]}
          rules={[{ required: true, message: '請選擇至少一個' }]}
        >
          <Checkbox.Group style={{ width: '100%' }}>
            <Row gutter={[0, 10]}>
              {ISSUE_OPTIONS.map(opt => {
                const isFixed = fixedIssues.has(opt.value);
                return (
                  <Col span={24} key={opt.value}>
                    <Checkbox value={opt.value} disabled={isFixed}>
                      <Space wrap>
                        {opt.icon}
                        <Text strong style={isFixed ? { color: '#bbb' } : {}}>{opt.label}</Text>
                        {isFixed
                          ? <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: 11 }}>已修復</Tag>
                          : defaultIssues.includes(opt.value) && <Tag color="red" style={{ fontSize: 11 }}>掃描發現問題</Tag>}
                        <Text type="secondary" style={{ fontSize: 12 }}>{opt.desc}</Text>
                      </Space>
                    </Checkbox>
                  </Col>
                );
              })}
            </Row>
          </Checkbox.Group>
        </Form.Item>

        {/* All detected issues already fixed */}
        {fixedIssues.size > 0 && ISSUE_OPTIONS.every(o => fixedIssues.has(o.value)) && (
          <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            message="此掃描的所有問題均已修復完成"
            description="如需重新修復，請先執行新的 AEO 掃描後再建立修復工作。"
            style={{ marginTop: 8 }}
          />
        )}

        {/* Auto-select button — only shown if there are unfixed detectable issues */}
        {selectedScan && defaultIssues.length > 0 && (
          <Button
            type="link"
            style={{ padding: 0, marginTop: 4 }}
            onClick={() => form.setFieldValue('selected_issues', defaultIssues)}
          >
            自動勾選尚未修復的問題 ({defaultIssues.length} 項)
          </Button>
        )}
      </Form>
    </Modal>
  );
}

// ── Fix Job Detail Drawer ─────────────────────────────────────────────────

function JobDetailDrawer({ jobId, open, onClose, onRefresh }) {
  const [job, setJob] = useState(null);
  const [scan, setScan] = useState(null);
  const [reScan, setReScan] = useState(null);
  const [rescanning, setRescanning] = useState(false);
  const pollRef = useRef(null);

  const fetchJob = useCallback(async (id) => {
    try {
      const res = await aeoFixAPI.getJob(id);
      setJob(res.data);
      return res.data;
    } catch (e) { return null; }
  }, []);

  useEffect(() => {
    if (!open || !jobId) { setJob(null); setScan(null); setReScan(null); return; }
    fetchJob(jobId).then(async (j) => {
      if (!j) return;
      try { const s = await aeoScannerAPI.get(j.scan_id); setScan(s.data); } catch (e) {}
      if (j.re_scan_id) {
        try { const rs = await aeoScannerAPI.get(j.re_scan_id); setReScan(rs.data); } catch (e) {}
      }
    });
  }, [open, jobId, fetchJob]);

  useEffect(() => {
    if (!open || !jobId) return;
    pollRef.current = setInterval(async () => {
      const j = await fetchJob(jobId);
      if (j && (j.status === 'done' || j.status === 'failed')) {
        clearInterval(pollRef.current);
        onRefresh();
        if (j.re_scan_id && !reScan) {
          try { const rs = await aeoScannerAPI.get(j.re_scan_id); setReScan(rs.data); } catch (e) {}
        }
      }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [open, jobId, fetchJob, onRefresh, reScan]);

  const handleRescan = async () => {
    setRescanning(true);
    try {
      const res = await aeoFixAPI.rescan(jobId);
      setJob(res.data);
      const rs = await aeoScannerAPI.get(res.data.re_scan_id);
      setReScan(rs.data);
      message.success('重新掃描完成！');
      onRefresh();
    } catch (e) {
      message.error('重新掃描失敗：' + (e.message || '未知錯誤'));
    } finally {
      setRescanning(false);
    }
  };

  const logLines = job?.fix_log
    ? (typeof job.fix_log === 'string' ? job.fix_log.split('\n') : job.fix_log)
    : [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={<Space><ToolOutlined /><span>修復工作詳情 #{jobId}</span></Space>}
      width={720}
      extra={
        job?.status === 'done' && (
          <Button type="primary" icon={<ReloadOutlined />} loading={rescanning} onClick={handleRescan}>
            {job?.re_scan_id ? '再次掃描' : '重新掃描驗收'}
          </Button>
        )
      }
    >
      {!job ? (
        <div style={{ textAlign: 'center', paddingTop: 60 }}><Spin size="large" /></div>
      ) : (
        <>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Descriptions size="small" column={2}>
              <Descriptions.Item label="狀態">
                <Badge status={STATUS_COLORS[job.status]} text={STATUS_LABELS[job.status]} />
                {job.status === 'running' && <SyncOutlined spin style={{ marginLeft: 8, color: '#1890ff' }} />}
              </Descriptions.Item>
              <Descriptions.Item label="建立時間">
                {job.created_at ? new Date(job.created_at).toLocaleString('zh-TW') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="完成時間" span={2}>
                {job.completed_at ? new Date(job.completed_at).toLocaleString('zh-TW') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="修復項目" span={2}>
                <Space wrap>
                  {(job.selected_issues || []).map(i => {
                    const opt = ISSUE_OPTIONS.find(o => o.value === i);
                    return <Tag key={i}>{opt?.label || i}</Tag>;
                  })}
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Before/After score */}
          {(scan || reScan) && (
            <Card title={<span><ThunderboltOutlined /> 修復前後對比</span>} size="small" style={{ marginBottom: 16 }}>
              <Row gutter={24} align="middle" justify="center">
                <Col span={10} style={{ textAlign: 'center' }}>
                  <Text type="secondary">修復前</Text>
                  {scan ? (
                    <>
                      <div>
                        <Progress type="circle" percent={scan.overall_score} size={80}
                          strokeColor={scoreColor(scan.overall_score)}
                          format={() => <span style={{ fontWeight: 700 }}>{scan.overall_score}</span>} />
                      </div>
                      <Tag color={scan.grade === 'A' ? 'green' : scan.grade === 'B' ? 'blue' : scan.grade === 'C' ? 'gold' : 'red'} style={{ marginTop: 8 }}>
                        {scan.grade} 級
                      </Tag>
                    </>
                  ) : <Text type="secondary">-</Text>}
                </Col>
                <Col span={4} style={{ textAlign: 'center', fontSize: 24, color: '#bbb' }}>
                  <ArrowRightOutlined />
                </Col>
                <Col span={10} style={{ textAlign: 'center' }}>
                  <Text type="secondary">修復後</Text>
                  {reScan ? (
                    <>
                      <div>
                        <Progress type="circle" percent={reScan.overall_score} size={80}
                          strokeColor={scoreColor(reScan.overall_score)}
                          format={() => <span style={{ fontWeight: 700 }}>{reScan.overall_score}</span>} />
                      </div>
                      <Tag color={reScan.grade === 'A' ? 'green' : reScan.grade === 'B' ? 'blue' : reScan.grade === 'C' ? 'gold' : 'red'} style={{ marginTop: 8 }}>
                        {reScan.grade} 級
                      </Tag>
                      {scan && (
                        <div style={{ marginTop: 4 }}>
                          {reScan.overall_score > scan.overall_score
                            ? <Tag color="green">+{reScan.overall_score - scan.overall_score} 分</Tag>
                            : reScan.overall_score < scan.overall_score
                              ? <Tag color="red">{reScan.overall_score - scan.overall_score} 分</Tag>
                              : <Tag>±0</Tag>}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ color: '#999', fontSize: 13, marginTop: 8 }}>
                      {job.status === 'done' ? '點擊右上角「重新掃描驗收」' : '修復完成後可重新掃描'}
                    </div>
                  )}
                </Col>
              </Row>
            </Card>
          )}

          {/* Fix Log */}
          {logLines.length > 0 && (
            <Card
              title={<Space>執行日誌 {job.status === 'running' && <SyncOutlined spin />}</Space>}
              size="small" style={{ marginBottom: 16 }}
            >
              <div style={{
                background: '#1a1a2e', color: '#a8ff78', fontFamily: 'monospace',
                padding: 12, borderRadius: 6, maxHeight: 280, overflowY: 'auto',
                fontSize: 12, lineHeight: 1.7,
              }}>
                {logLines.map((line, i) => (
                  <div key={i} style={{
                    color: line.includes('ERROR') || line.includes('失敗') ? '#ff6b6b'
                      : line.includes('✓') || line.includes('完成') || line.includes('成功') ? '#a8ff78'
                        : line.includes('→') || line.includes('⚡') ? '#ffd93d'
                          : '#a8ff78',
                  }}>{line}</div>
                ))}
                {job.status === 'running' && <div style={{ color: '#ffd93d' }}>▌</div>}
              </div>
            </Card>
          )}

          {/* Fixed Files */}
          {job.fixed_files?.length > 0 && (
            <Card title="已修復的檔案" size="small" style={{ marginBottom: 16 }}>
              <Timeline items={job.fixed_files.map(f => ({
                color: 'green',
                dot: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                children: <Text code>{typeof f === 'string' ? f : f.path || f.file}</Text>,
              }))} />
            </Card>
          )}

          {/* Error */}
          {job.status === 'failed' && job.error_message && (
            <Alert type="error" showIcon message="修復失敗" description={job.error_message} />
          )}
        </>
      )}
    </Drawer>
  );
}

// ── Targets Tab ───────────────────────────────────────────────────────────

function TargetsTab() {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Track per-target backup state
  const [backupState, setBackupState] = useState({}); // { [targetId]: { loading, backup } }

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aeoFixAPI.listTargets();
      const list = res.data || [];
      setTargets(list);
      // Fetch latest backup for each target
      list.forEach(async (t) => {
        setBackupState(prev => ({ ...prev, [t.id]: { loading: true, backup: null } }));
        try {
          const br = await aeoFixAPI.listBackups(t.id);
          const backups = br.data || [];
          setBackupState(prev => ({ ...prev, [t.id]: { loading: false, backup: backups[0] || null } }));
        } catch (e) {
          setBackupState(prev => ({ ...prev, [t.id]: { loading: false, backup: null } }));
        }
      });
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTargets(); }, [fetchTargets]);

  const handleDelete = async (id) => {
    try {
      await aeoFixAPI.deleteTarget(id);
      message.success('已刪除');
      fetchTargets();
    } catch (e) { message.error('刪除失敗'); }
  };

  const handleBackup = async (targetId) => {
    setBackupState(prev => ({ ...prev, [targetId]: { loading: true, backup: prev[targetId]?.backup || null } }));
    try {
      const res = await aeoFixAPI.startBackup(targetId);
      const backup = res.data;
      setBackupState(prev => ({ ...prev, [targetId]: { loading: false, backup } }));
      message.info('備份已啟動，稍後自動更新狀態...');
      // Poll until done
      const poll = setInterval(async () => {
        try {
          const br = await aeoFixAPI.getBackup(backup.id);
          const b = br.data;
          setBackupState(prev => ({ ...prev, [targetId]: { loading: false, backup: b } }));
          if (b.status === 'done') {
            clearInterval(poll);
            message.success(`備份完成：${b.backup_size || '?'}`);
          } else if (b.status === 'failed') {
            clearInterval(poll);
            message.error('備份失敗：' + (b.error_message || '未知錯誤'));
          }
        } catch (e) { clearInterval(poll); }
      }, 3000);
    } catch (e) {
      setBackupState(prev => ({ ...prev, [targetId]: { loading: false, backup: prev[targetId]?.backup || null } }));
      message.error('啟動備份失敗：' + (e.message || '未知錯誤'));
    }
  };

  const columns = [
    { title: '網域', dataIndex: 'domain', render: t => <Text strong>{t}</Text> },
    { title: 'SSH Host', dataIndex: 'ssh_host' },
    { title: '用戶', dataIndex: 'ssh_user', width: 80 },
    { title: 'Port', dataIndex: 'ssh_port', width: 70 },
    { title: 'Web Root', dataIndex: 'web_root' },
    {
      title: '最新備份',
      key: 'backup',
      render: (_, r) => {
        const bs = backupState[r.id] || {};
        return <BackupStatusBadge backup={bs.backup} loading={bs.loading} />;
      },
    },
    {
      title: '操作', width: 180,
      render: (_, r) => {
        const bs = backupState[r.id] || {};
        const isRunning = bs.backup?.status === 'running' || bs.loading;
        return (
          <Space>
            <Button
              size="small"
              type="primary"
              icon={isRunning ? <SyncOutlined spin /> : <SaveOutlined />}
              loading={isRunning}
              onClick={() => handleBackup(r.id)}
              disabled={isRunning}
            >
              備份
            </Button>
            <Button size="small" icon={<EditOutlined />} onClick={() => { setEditing(r); setModalOpen(true); }} />
            <Popconfirm title="確定刪除此目標？" onConfirm={() => handleDelete(r.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text type="secondary">設定要透過 SSH 連線自動修復的目標伺服器（需已設定 ssh-copy-id），並可在此對每個目標執行備份</Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); setModalOpen(true); }}>
          新增目標
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={targets}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: '尚無 SSH 目標，請先新增' }}
      />
      <TargetModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={fetchTargets}
        editing={editing}
      />
    </>
  );
}

// ── Jobs Tab ──────────────────────────────────────────────────────────────

function JobsTab({ onRequestNewJob }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pollRef = useRef(null);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aeoFixAPI.listJobs();
      setJobs(res.data || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Auto-refresh every 4 s while any job is running
  useEffect(() => {
    const hasRunning = jobs.some(j => j.status === 'running');
    clearInterval(pollRef.current);
    if (hasRunning) {
      pollRef.current = setInterval(() => {
        aeoFixAPI.listJobs()
          .then(res => setJobs(res.data || []))
          .catch(() => {});
      }, 4000);
    }
    return () => clearInterval(pollRef.current);
  }, [jobs]);

  const openJob = (id) => { setSelectedJobId(id); setDrawerOpen(true); };

  const columns = [
    { title: '#', dataIndex: 'id', width: 60, render: id => <Text type="secondary">#{id}</Text> },
    {
      title: '狀態', dataIndex: 'status', width: 110,
      render: s => (
        <Badge status={STATUS_COLORS[s]} text={
          <Space>{STATUS_LABELS[s]}{s === 'running' && <SyncOutlined spin style={{ fontSize: 12 }} />}</Space>
        } />
      ),
    },
    {
      title: '修復項目', dataIndex: 'selected_issues',
      render: issues => (
        <Space wrap>
          {(issues || []).map(i => {
            const opt = ISSUE_OPTIONS.find(o => o.value === i);
            return <Tag key={i} style={{ fontSize: 11 }}>{opt?.label || i}</Tag>;
          })}
        </Space>
      ),
    },
    {
      title: '結果', dataIndex: 'fixed_files',
      render: (files, r) => {
        if (r.status === 'done') return <Tag color="green" icon={<CheckCircleOutlined />}>{(files || []).length} 個檔案</Tag>;
        if (r.status === 'failed') return <Tag color="red" icon={<CloseCircleOutlined />}>失敗</Tag>;
        if (r.status === 'running') return <Tag color="blue" icon={<SyncOutlined spin />}>進行中</Tag>;
        return '-';
      },
    },
    {
      title: '驗收', dataIndex: 're_scan_id',
      render: id => id ? <Tag color="cyan" icon={<CheckCircleOutlined />}>已重新掃描</Tag> : '-',
    },
    { title: '建立時間', dataIndex: 'created_at', width: 150, render: t => t ? new Date(t).toLocaleString('zh-TW') : '-' },
    {
      title: '操作', width: 80,
      render: (_, r) => <Button size="small" icon={<EyeOutlined />} onClick={() => openJob(r.id)}>詳情</Button>,
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text type="secondary">每個修復工作透過 SSH 連線到目標伺服器，由 AI 自動生成並部署修復內容（修復前自動提示備份）</Text>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchJobs}>重新整理</Button>
          <Button type="primary" icon={<PlayCircleOutlined />} onClick={onRequestNewJob}>啟動新修復</Button>
        </Space>
      </div>

      {jobs.some(j => j.status === 'running') && (
        <Alert
          type="info" showIcon icon={<SyncOutlined spin />}
          message="有工作正在執行中，點擊詳情可即時查看日誌"
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        columns={columns}
        dataSource={jobs}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: '尚無修復記錄' }}
        onRow={r => ({ onClick: () => openJob(r.id), style: { cursor: 'pointer' } })}
      />

      <JobDetailDrawer
        jobId={selectedJobId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onRefresh={fetchJobs}
      />
    </>
  );
}

// ── Backups Tab ───────────────────────────────────────────────────────────

function BackupsTab() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [targets, setTargets] = useState([]);
  const [filterTarget, setFilterTarget] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [pathModalOpen, setPathModalOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);

  const fetchBackups = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTarget) params.target_id = filterTarget;
      if (filterStatus) params.status = filterStatus;
      const res = await aeoFixAPI.listAllBackups(params);
      setBackups(res.data || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [filterTarget, filterStatus]);

  useEffect(() => {
    aeoFixAPI.listTargets().then(res => setTargets(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchBackups(); }, [fetchBackups]);

  const getFilename = (path) => {
    if (!path) return '-';
    return path.split('/').pop();
  };

  const statusTag = (status) => {
    if (status === 'done') return <Tag color="success" icon={<CheckCircleOutlined />}>完成</Tag>;
    if (status === 'running') return <Tag color="processing" icon={<SyncOutlined spin />}>備份中</Tag>;
    if (status === 'failed') return <Tag color="error" icon={<CloseCircleOutlined />}>失敗</Tag>;
    return <Tag>{status}</Tag>;
  };

  const columns = [
    {
      title: '#', dataIndex: 'id', width: 60,
      render: id => <Text type="secondary">#{id}</Text>,
    },
    {
      title: '目標網域', dataIndex: 'target_domain', width: 180,
      render: (d, r) => (
        <div>
          <Text strong>{d || `target #${r.target_id}`}</Text>
          {r.target_ssh_host && <div style={{ fontSize: 11, color: '#999' }}>{r.target_ssh_host}</div>}
        </div>
      ),
    },
    {
      title: '狀態', dataIndex: 'status', width: 100,
      render: statusTag,
    },
    {
      title: '備份檔名', dataIndex: 'backup_path', ellipsis: true,
      render: (path) => path ? (
        <Text code style={{ fontSize: 12 }}>{getFilename(path)}</Text>
      ) : '-',
    },
    {
      title: '大小', dataIndex: 'backup_size', width: 80,
      render: s => s ? <Tag color="blue">{s}</Tag> : '-',
    },
    {
      title: '檔案數', dataIndex: 'file_count', width: 80,
      render: n => n != null ? <Text>{n.toLocaleString()}</Text> : '-',
    },
    {
      title: '備份時間', dataIndex: 'created_at', width: 150,
      render: t => t ? new Date(t).toLocaleString('zh-TW') : '-',
    },
    {
      title: '完成時間', dataIndex: 'completed_at', width: 150,
      render: t => t ? new Date(t).toLocaleString('zh-TW') : '-',
    },
    {
      title: '操作', width: 80, fixed: 'right',
      render: (_, r) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => { setSelectedBackup(r); setPathModalOpen(true); }}
        >
          詳情
        </Button>
      ),
    },
  ];

  // stats
  const done = backups.filter(b => b.status === 'done').length;
  const running = backups.filter(b => b.status === 'running').length;
  const failed = backups.filter(b => b.status === 'failed').length;

  return (
    <>
      {/* Stats row */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {[
          { label: '總備份數', value: backups.length, color: '#1890ff' },
          { label: '成功', value: done, color: '#52c41a' },
          { label: '進行中', value: running, color: '#1890ff' },
          { label: '失敗', value: failed, color: '#f5222d' },
        ].map(s => (
          <Col key={s.label} xs={12} sm={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#999' }}>{s.label}</div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filter bar */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <Select
          allowClear
          placeholder="篩選目標"
          style={{ width: 200 }}
          value={filterTarget}
          onChange={setFilterTarget}
          options={targets.map(t => ({ value: t.id, label: t.domain }))}
        />
        <Select
          allowClear
          placeholder="篩選狀態"
          style={{ width: 140 }}
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            { value: 'done', label: '成功' },
            { value: 'running', label: '進行中' },
            { value: 'failed', label: '失敗' },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchBackups}>重新整理</Button>
        <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>
          備份儲存於目標伺服器的 /tmp/aeo_backups/ 目錄
        </Text>
      </div>

      <Table
        columns={columns}
        dataSource={backups}
        rowKey="id"
        loading={loading}
        size="small"
        scroll={{ x: 1100 }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        locale={{ emptyText: '尚無備份記錄' }}
        rowClassName={r => r.status === 'failed' ? 'ant-table-row-danger' : ''}
      />

      {/* Detail modal */}
      <Modal
        open={pathModalOpen}
        title={<Space><SaveOutlined /><span>備份詳情 #{selectedBackup?.id}</span></Space>}
        onCancel={() => setPathModalOpen(false)}
        footer={<Button onClick={() => setPathModalOpen(false)}>關閉</Button>}
      >
        {selectedBackup && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="備份 ID">#{selectedBackup.id}</Descriptions.Item>
            <Descriptions.Item label="目標網域">
              <Text strong>{selectedBackup.target_domain || '-'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="SSH Host">
              {selectedBackup.target_ssh_host || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Web Root">
              <Text code>{selectedBackup.target_web_root || '-'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="狀態">{statusTag(selectedBackup.status)}</Descriptions.Item>
            <Descriptions.Item label="備份檔名">
              <Text code style={{ wordBreak: 'break-all' }}>
                {selectedBackup.backup_path ? selectedBackup.backup_path.split('/').pop() : '-'}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="完整備份路徑">
              <Text
                copyable={!!selectedBackup.backup_path}
                code
                style={{ wordBreak: 'break-all', fontSize: 12 }}
              >
                {selectedBackup.backup_path || '-'}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="大小">{selectedBackup.backup_size || '-'}</Descriptions.Item>
            <Descriptions.Item label="檔案數">
              {selectedBackup.file_count != null ? selectedBackup.file_count.toLocaleString() + ' 個' : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="備份開始時間">
              {selectedBackup.created_at ? new Date(selectedBackup.created_at).toLocaleString('zh-TW') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="備份完成時間">
              {selectedBackup.completed_at ? new Date(selectedBackup.completed_at).toLocaleString('zh-TW') : '-'}
            </Descriptions.Item>
            {selectedBackup.error_message && (
              <Descriptions.Item label="錯誤訊息">
                <Text type="danger" style={{ fontSize: 12 }}>{selectedBackup.error_message}</Text>
              </Descriptions.Item>
            )}
            {selectedBackup.status === 'done' && (
              <Descriptions.Item label="回滾指令">
                <Text
                  copyable
                  code
                  style={{ fontSize: 11, wordBreak: 'break-all' }}
                >
                  {`tar -xzf ${selectedBackup.backup_path} -C ${selectedBackup.target_web_root ? selectedBackup.target_web_root.split('/').slice(0, -1).join('/') || '/' : '/'}`}
                </Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════════════

export default function AeoFixPage() {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subGateOpen, setSubGateOpen] = useState(false);
  const [checkingSub, setCheckingSub] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await billingAPI.getStatus();
        const active = res.data?.ai_seo_subscription_active === true;
        setSubscriptionStatus(active);
      } catch { setSubscriptionStatus(false); }
      setCheckingSub(false);
    })();
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('jobs');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [jobsKey, setJobsKey] = useState(0);

  const params = new URLSearchParams(location.search);
  const preloadScanId = params.get('scan_id') ? parseInt(params.get('scan_id'), 10) : null;

  useEffect(() => {
    if (preloadScanId) setCreateModalOpen(true);
  }, [preloadScanId]);

  const handleJobCreated = () => {
    setJobsKey(k => k + 1);
    navigate('/aeo-fix', { replace: true });
  };

  const tabItems = [
    {
      key: 'jobs',
      label: <span><ToolOutlined /> 修復工作</span>,
      children: <JobsTab key={jobsKey} onRequestNewJob={() => { if (!subscriptionStatus) { setSubGateOpen(true); return; } setCreateModalOpen(true); }} />,
    },
    {
      key: 'backups',
      label: <span><DatabaseOutlined /> 備份記錄</span>,
      children: <BackupsTab />,
    },
    {
      key: 'targets',
      label: <span><CloudServerOutlined /> SSH 目標設定</span>,
      children: <TargetsTab />,
    },
  ];

  if (checkingSub) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}><Text type="secondary">检查订阅状态...</Text></div>
      </div>
    );
  }



  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 4 }}>
          <ToolOutlined /> AEO Auto-Fix — AI 自動修復

      {/* Subscription Gate Modal */}
      <Modal
        title="需要訂閱或優惠券"
        open={subGateOpen}
        onCancel={() => setSubGateOpen(false)}
        footer={null}
        width={420}
      >
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <RocketOutlined style={{ fontSize: 48, color: '#fa8c16', marginBottom: 12 }} />
          <Title level={4}>需要有效訂閱或優惠券才能使用 AEO Auto-Fix</Title>
          <Paragraph type="secondary">
            AEO Auto-Fix 使用 AI 模型自動分析與修復，屬於進階功能。
          </Paragraph>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Button type="primary" size="large" icon={<DollarOutlined />} block
              onClick={() => { setSubGateOpen(false); navigate('/billing'); }}>
              前往訂閱方案
            </Button>
            <Button size="large" icon={<GiftOutlined />} block
              onClick={() => { setSubGateOpen(false); navigate('/billing'); }}>
              兌換優惠券
            </Button>
            <Button size="large" icon={<SendOutlined />} block
              onClick={() => { setSubGateOpen(false); navigate('/coupon-request'); }}>
              申請免費優惠券
            </Button>
          </Space>
          <Divider />
          <Text type="secondary" style={{ fontSize: 12 }}>
            你可以在「帳務中心」查看訂閱狀態與購買方案。
          </Text>
        </div>
      </Modal>


        </Title>
        <Paragraph type="secondary">
          基於 AEO 掃描結果，透過 SSH 連線到目標伺服器，AI 自動生成並部署修復內容。
          <Text strong> 修復前系統會提示執行備份</Text>，確保可隨時回滾。
        </Paragraph>
      </div>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>

      <CreateJobModal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          if (preloadScanId) navigate('/aeo-fix', { replace: true });
        }}
        onCreated={handleJobCreated}
        preloadScanId={preloadScanId}
      />
    </div>
  );
}
